import {
  sendShopifyAnalytics,
  getClientBrowserParameters,
  AnalyticsEventName,
  useShopifyCookies,
  type ShopifyAnalyticsProduct,
  type ShopifyPageViewPayload,
  ShopifySalesChannel,
} from "@shopify/hydrogen-react";
import { BaseAdapter } from "./base-adapter";
import type { TEvent } from "../types";
import { EventType } from "../types";

// Constants based on environment variables
const DEFAULT_CURRENCY = "INR";
const DEFAULT_LANGUAGE = "en";
const SHOP_ID = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID || "";

/**
 * Type definitions for analytics payloads
 */
type SendPageViewPayload = {
  pageType?: string;
  products?: ShopifyAnalyticsProduct[];
  collectionHandle?: string;
  searchString?: string;
  totalValue?: number;
  cartId?: string;
};

type SendAddToCartPayload = {
  cartId: string;
  products?: ShopifyAnalyticsProduct[];
  totalValue?: ShopifyPageViewPayload["totalValue"];
};

/**
 * Configuration interface for Shopify Analytics Adapter
 */
export interface ShopifyAnalyticsConfig {
  shopId: string;
  currency?: string;
}

/**
 * Shopify Analytics Adapter for tracking events to Shopify Admin Analytics
 */
export class ShopifyAnalyticsAdapter extends BaseAdapter {
  public readonly name = "ShopifyAnalytics";
  private shopId: string;
  private currency: string;

  constructor(config: ShopifyAnalyticsConfig) {
    super(config);
    this.shopId = config.shopId || SHOP_ID;
    this.currency = config.currency || DEFAULT_CURRENCY;
  }

  /**
   * Initialize the Shopify Analytics adapter
   */
  public async initialize(): Promise<void> {
    try {
      // Enable Shopify cookies for attribution tracking
      if (typeof window !== "undefined") {
        // Note: useShopifyCookies is a React hook, so we'll handle this in the component level
        console.log("🛍️ Shopify Analytics adapter initialized successfully");
        console.log("🛍️ Shop ID:", this.shopId);
        console.log("🛍️ Currency:", this.currency);
      }
      this.initialized = true;
    } catch (error) {
      console.error("❌ Failed to initialize Shopify Analytics:", error);
      throw error;
    }
  }

  /**
   * Convert product ID to Shopify GID format if it's not already
   */
  private formatProductGid(productId: string): string {
    return productId.startsWith("gid://shopify/Product/")
      ? productId
      : `gid://shopify/Product/${productId}`;
  }

  /**
   * Create base payload for all events
   */
  private createBasePayload(): any {
    return {
      ...getClientBrowserParameters(),
      hasUserConsent: true,
      analyticsAllowed: true,
      marketingAllowed: true,
      saleOfDataAllowed: true,
      storefrontId: this.shopId,
      shopifySalesChannel: ShopifySalesChannel.headless,
      shopId: `gid://shopify/Shop/${this.shopId}`,
      currency: this.currency,
      acceptedLanguage: DEFAULT_LANGUAGE,
      ...this.enhanceWithAffiliateParams({}),
    };
  }

  /**
   * Send page view event using the enhanced functionality
   */
  public sendPageView = (
    eventName: keyof typeof AnalyticsEventName,
    payload?: SendPageViewPayload,
  ) => {
    const enhancedPayload = {
      ...this.createBasePayload(),
      ...payload,
    };

    return sendShopifyAnalytics({
      eventName,
      payload: enhancedPayload,
    });
  };

  /**
   * Send add to cart event using the enhanced functionality
   */
  public sendAddToCart = ({
    cartId,
    totalValue,
    products,
  }: SendAddToCartPayload) => {
    return this.sendPageView(AnalyticsEventName.ADD_TO_CART, {
      cartId,
      totalValue,
      products,
    });
  };

  /**
   * Send analytics event with logging
   */
  private sendAnalyticsEvent(
    eventName: string,
    payload: any,
    eventType: string,
    logData?: any,
  ): void {
    console.log(
      `🛍️ Shopify Analytics - Sending ${eventType}:`,
      logData || payload,
    );

    // Log the full payload for debugging
    console.log(
      `🛍️ Full payload for ${eventType}:`,
      JSON.stringify(payload, null, 2),
    );

    sendShopifyAnalytics({
      eventName,
      payload,
    });

    console.log(`🛍️ Shopify Analytics - ${eventType} sent successfully`);
  }

  /**
   * Track an event using Shopify Analytics
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined") {
      console.warn(
        "🛍️ Shopify Analytics not initialized or not in browser environment",
      );
      return;
    }

    try {
      console.log("🛍️ Shopify Analytics - Processing event:", event.type);

      switch (event.type) {
        case EventType.PAGE_VIEW:
          this.sendPageView(AnalyticsEventName.PAGE_VIEW);
          break;

        case EventType.ADD_TO_CART:
          this.trackAddToCart(event);
          break;

        default:
          console.log("🛍️ Shopify Analytics - Event not supported:", event.type);
          break;
      }
    } catch (error) {
      console.error("❌ Error tracking Shopify Analytics event:", error);
    }
  }

  /**
   * Track add to cart event
   */
  private trackAddToCart(event: any): void {
    if (!event.productId) {
      console.warn("🛍️ Shopify Analytics - ADD_TO_CART: Missing productId");
      return;
    }

    const productGid = this.formatProductGid(event.productId);
    const cartId = JSON.parse(localStorage.getItem("cart_token") || '""');

    // Create products array for analytics
    const products: ShopifyAnalyticsProduct[] = [
      {
        productGid: productGid,
        variantGid:
          event.variantId || productGid.replace("Product", "ProductVariant"),
        quantity: event.quantity || 1,
        price: event.price || 0,
        name: event.productName || "Unknown Product",
        brand: event.brand || "Unknown Brand",
      },
    ];

    const totalValue = (event.price || 0) * (event.quantity || 1);

    // Use the enhanced sendAddToCart method
    this.sendAddToCart({
      cartId,
      products,
      totalValue,
    });
  }
}
